import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { ImportedProduct } from '../types/product';
import { Heart, ShoppingCart, Check } from 'lucide-react';
import { wishlistService } from '../api/wishlist';
import { cartService } from '../api/cart';
import { useCart } from '../context/CartContext';
import { useRequireCustomerAuth } from '../hooks/useRequireCustomerAuth';
import { formatINR } from '../utils/currency';
import { getProductSellingPrice, getProductMrp } from '../utils/pricing';

// Union type to handle both Product and ImportedProduct
type ProductCardProps = {
  product: Product | ImportedProduct;
  variant?: 'grid' | 'list' | 'carousel';
  showWishlist?: boolean;
  showAddToCart?: boolean;
  showRating?: boolean;
  showBrand?: boolean;
  showDiscount?: boolean;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variant = 'grid',
  showWishlist = true,
  showAddToCart = true,
  showRating = true,
  showBrand = true,
  showDiscount = true,
  className = ''
}) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { guardAction } = useRequireCustomerAuth();

  const showNotification = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const productId = (('_id' in product && product._id)
      ? product._id
      : ('id' in product && product.id)
        ? product.id
        : product.slug) as string;

    if (!productId) {
      alert('Cannot add to wishlist: Product ID missing');
      return;
    }

    await guardAction(
      { type: 'wishlist', productId, add: !isWishlisted },
      async () => {
        try {
          if (isWishlisted) {
            await wishlistService.removeFromWishlist(productId);
            showNotification('Removed from wishlist');
          } else {
            await wishlistService.addToWishlist(productId);
            showNotification('Added to wishlist!');
          }
          setIsWishlisted(!isWishlisted);
        } catch (error: any) {
          console.error('Wishlist error:', error);
          showNotification(error.message || 'Failed to update wishlist');
        }
      }
    );
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const productId = (('_id' in product && product._id)
      ? product._id
      : ('id' in product && product.id)
        ? product.id
        : product.slug) as string;

    if (!productId) {
      alert('Cannot add to cart: Product ID missing');
      return;
    }

    const localItem = {
      productId,
      name: product.name,
      price: sellingPrice || 0,
      mrp: mrpPrice || sellingPrice || 0,
      image: mainImage,
      quantity: 1,
    };

    await guardAction(
      { type: 'addToCart', productId, quantity: 1, localItem },
      async () => {
        setIsAddingToCart(true);
        try {
          await cartService.addToCart(productId, 1);
          addToCart(localItem);
          showNotification('Added to cart successfully!');
        } catch (error: any) {
          console.error('Cart error:', error);
          showNotification(error.message || 'Failed to add to cart');
        } finally {
          setIsAddingToCart(false);
        }
      }
    );
  };

  // Calculate price info (price.selling is tier-adjusted when APIs send Authorization)
  const sellingPrice = getProductSellingPrice(product);
  const mrpPrice = getProductMrp(product) ?? ('compare_price' in product ? (product as { compare_price?: number }).compare_price : null);
  
  // Calculate discount percentage
  let discountPercent = 0;
  if (product.discount && typeof product.discount === 'object' && product.discount.percentage > 0) {
    discountPercent = product.discount.percentage;
  } else if (product.discount && typeof product.discount === 'number' && product.discount > 0) {
    discountPercent = product.discount;
  } else if (mrpPrice && sellingPrice && mrpPrice > sellingPrice) {
    discountPercent = Math.round(((mrpPrice - sellingPrice) / mrpPrice) * 100);
  }

  // Get rating info
  const ratingValue = typeof product.rating === 'object' ? product.rating.average : product.rating;
  const ratingCount = typeof product.rating === 'object' ? product.rating.count : 0;

  // Check if out of stock
  const isOutOfStock = 'inventory' in product && product.inventory?.stock === 0;

  // Transform images from backend format to frontend format
  const productImages = (product.images || []).map((img: string | { url: string }) => {
    const imageUrl = typeof img === 'string' ? img : img.url;
    if (!imageUrl) return '';
    // Handle both /uploads/ and uploads/ paths - convert to full URL
    if (imageUrl.startsWith('/uploads') || imageUrl.startsWith('uploads/')) {
      const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${process.env.REACT_APP_SERVER_URL || 'http://localhost:5001'}${cleanPath}`;
    }
    return imageUrl;
  }).filter(url => url !== '');
  
  // Fallback image strategy
  const getFallbackImage = () => {
    // Try uploaded images first
    if (productImages.length > 0 && productImages[0]) {
      return productImages[0];
    }
    // Try Amazon images (for imported products)
    if (product.images && product.images.length > 0) {
      const amazonImage = (product.images as any[]).find((img) => 
        typeof img === 'string' && img.includes('amazon.com')
      );
      if (amazonImage) return amazonImage;
    }
    // Final fallback
    return '/placeholder-product.jpg';
  };
  
  const mainImage = getFallbackImage();

  const handleCardClick = () => {
    navigate(`/product/${product.slug || ''}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group cursor-pointer block bg-white rounded-xl shadow-sm hover:shadow-lg overflow-hidden transition-all duration-300 hover:scale-[1.02] h-full flex flex-col shrink-0 ${variant === 'carousel' ? 'w-full max-w-full' : ''} ${className}`}
    >
      {/* Product Image Container - Amazon Style */}
      <div className="relative h-[160px] md:h-[200px] flex items-center justify-center p-3 bg-white">
        <img
          src={mainImage}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            const target = e.currentTarget;
            if (productImages.length > 1 && target.src !== productImages[1]) {
              target.src = productImages[1];
            } else if (!target.src.includes('placeholder')) {
              target.src = '/placeholder-product.jpg';
            }
          }}
        />

        {/* Discount Badge - Top Left, Only if > 0 */}
        {showDiscount && discountPercent > 0 && (
          <span className="absolute top-2 left-2 bg-[#CC0C39] text-white text-xs font-bold px-2 py-1 rounded">
            -{discountPercent}% OFF
          </span>
        )}

        {/* Wishlist Button - Top Right */}
        {showWishlist && (
          <button
            onClick={handleWishlist}
            className={`absolute top-2 right-2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-all ${
              isWishlisted ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
            }`}
            aria-label="Add to wishlist"
          >
            <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>

      {/* Product Info - Amazon Style */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Product Title - 2 lines max */}
        <h3
          className="text-sm font-medium text-gray-900 mb-1.5 leading-snug"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.4'
          }}
        >
          {product.name}
        </h3>

        {/* Rating - Only if count > 0 */}
        {showRating && Number(ratingValue) > 0 && ratingCount > 0 && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="flex items-center bg-[#00A650] text-white px-1.5 py-0.5 rounded text-xs font-medium">
              <span>{Number(ratingValue).toFixed(1)}</span>
              <span className="ml-0.5">★</span>
            </div>
            <span className="text-xs text-gray-500">({ratingCount})</span>
          </div>
        )}

        {/* Price Section - Amazon Style */}
        <div className="mb-1.5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg font-bold text-gray-900">{formatINR(sellingPrice)}</span>
            {mrpPrice && mrpPrice > sellingPrice && (
              <span className="text-sm text-gray-500 line-through">{formatINR(mrpPrice)}</span>
            )}
          </div>
          {discountPercent > 0 && (
            <span className="text-xs text-[#CC0C39] font-medium">{discountPercent}% off</span>
          )}
        </div>

        {/* Add to Cart Button */}
        {variant !== 'list' && showAddToCart && (
          <button
            onClick={(e) => {
              e.preventDefault();
              handleAddToCart(e);
            }}
            disabled={isAddingToCart || isOutOfStock}
            className={`w-full mt-auto py-2 px-4 rounded-full font-medium text-sm transition-all ${
              isOutOfStock
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 border border-[#FCD200] shadow-sm hover:shadow'
            }`}
          >
            {isAddingToCart ? (
              'Adding...'
            ) : isOutOfStock ? (
              'Out of Stock'
            ) : (
              <span className="flex items-center justify-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Add to Cart
              </span>
            )}
          </button>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 animate-fade-in">
          <Check className="w-4 h-4 text-green-400" />
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default ProductCard;
